// CONTABIRD_ADMIN_BYPASS_STEVEN_CARLOS_REAL_20260709
import { useMemo } from 'react';
import { usePlans } from './usePlans';
import { useAuth } from './useAuth';
import { isAccountantWorkspaceActive } from '../lib/accountantAccess';
import { 
  PLAN_CONFIGS, 
  ROUTE_TO_MODULE, 
  MODULE_NAMES,
  getMinimumPlanName,
  type PlanId 
} from '../config/planPermissions';

export interface PlanPermissions {
  currentPlanId: PlanId;
  currentPlanName: string;
  isTrialActive: boolean;
  isTrialExpired: boolean;
  hasActivePlan: boolean;
  canAccessModule: (module: string) => boolean;
  canAccessRoute: (route: string) => boolean;
  getRequiredPlanForModule: (module: string) => string;
  getRequiredPlanForRoute: (route: string) => string;
  getModuleName: (module: string) => string;
  requiresAccountingSetup: boolean;
  skipPeriodValidation: boolean;
  limits: {
    users: number;
    invoicesPerMonth: number;
    warehouses: number;
    products: number;
  };
}

export function usePlanPermissions(): PlanPermissions {
  const { user } = useAuth();
  const { currentPlan, getTrialStatus } = usePlans();

  const adminPlanBypass =
    /* CONTABIRD_ADMIN_BYPASS_PLAN_LOCKS_REAL_20260708 */
    ['fitzroywhyte5@gmail.com', 'steven@contabird.com', 'stevenmaxwell199@contabird.com', 'carlosjuanhtc@contabird.com'].includes(String(user?.email || '').toLowerCase());
  const accountantPlanBypass = isAccountantWorkspaceActive();
  
  const trialStatus = getTrialStatus();
  const isTrialActive = trialStatus === 'active' || trialStatus === 'warning';
  const isTrialExpired = trialStatus === 'expired';
  const hasActivePlan = currentPlan?.active || false;

  // Determinar el plan actual
  const currentPlanId = useMemo((): PlanId => {
    if (adminPlanBypass || accountantPlanBypass) return 'pos-premium';
    if (hasActivePlan && currentPlan?.id) {
      // Búsqueda exacta contra el catálogo. Antes era una escalera de `includes()` que no
      // mencionaba ni los cuatro planes prepago ni los de 150 y 200 facturas, y todos caían en
      // el `return 'pos-premium'` del final: quien pagaba RD$1,250 por una bolsa de 150 facturas
      // se llevaba de propina el POS, el inventario, la nómina y la contabilidad completa.
      const planId = currentPlan.id.toLowerCase();
      if (planId in PLAN_CONFIGS) return planId as PlanId;

      // Un plan que no está en el catálogo es un despiste nuestro, no del cliente: se le deja
      // pasar para no dejar tirado a quien sí paga, y queda avisado en consola. El cupo de
      // facturas lo sigue aplicando la base de datos.
      console.warn(`[planes] El plan "${planId}" no está en PLAN_CONFIGS; se concede acceso completo.`);
      return 'pos-premium';
    }
    if (isTrialActive) return 'trial';
    return 'none';
  }, [accountantPlanBypass, adminPlanBypass, hasActivePlan, currentPlan, isTrialActive]);

  const planConfig = PLAN_CONFIGS[currentPlanId];
  const currentPlanName = planConfig?.name || 'Sin Plan';

  // Verificar si puede acceder a un módulo
  const canAccessModule = (module: string): boolean => {
    if (adminPlanBypass || accountantPlanBypass) return true;
    if (!planConfig) return false;
    if (isTrialExpired && currentPlanId === 'none') return false;
    return planConfig.modules.includes(module);
  };

  // Verificar si puede acceder a una ruta
  const canAccessRoute = (route: string): boolean => {
    if (adminPlanBypass || accountantPlanBypass) return true;
    if (!planConfig) return false;
    
    // Rutas siempre permitidas
    const alwaysAllowed = ['/plans', '/profile', '/settings', '/login', '/register', '/'];
    if (alwaysAllowed.some(r => route === r || route.startsWith(r + '/'))) {
      // Excepto settings para plan none si trial expirado
      if (route.startsWith('/settings') && isTrialExpired && currentPlanId === 'none') {
        return true; // Permitir settings incluso sin plan
      }
      return true;
    }

    // Durante trial activo, permitir todo
    if (isTrialActive && currentPlanId === 'trial') {
      return true;
    }

    // Si trial expirado y sin plan, bloquear todo excepto las rutas permitidas
    if (isTrialExpired && currentPlanId === 'none') {
      return false;
    }

    // Verificar si la ruta está en las rutas permitidas del plan
    // Primero verificar coincidencia exacta
    if (planConfig.routes.includes(route)) return true;

    // Una subruta con capacidad propia no hereda el permiso general del padre. Así, permitir
    // `/inventory` para almacenar productos no concede reportes, almacenes ni transferencias.
    const exactModule = ROUTE_TO_MODULE[route];
    if (exactModule) return canAccessModule(exactModule);

    // Luego verificar si alguna ruta del plan es prefijo de la ruta actual
    for (const allowedRoute of planConfig.routes) {
      if (allowedRoute === '*') return true;
      if (route.startsWith(allowedRoute + '/')) return true;
      if (route === allowedRoute) return true;
    }

    // Verificar rutas padre
    const routeParts = route.split('/').filter(Boolean);
    for (let i = routeParts.length - 1; i >= 0; i--) {
      const parentRoute = '/' + routeParts.slice(0, i + 1).join('/');
      if (planConfig.routes.includes(parentRoute)) return true;
      const parentModule = ROUTE_TO_MODULE[parentRoute];
      if (parentModule && canAccessModule(parentModule)) return true;
    }

    return false;
  };

  // Obtener el plan requerido para un módulo
  const getRequiredPlanForModule = (module: string): string => {
    return getMinimumPlanName(module);
  };

  // Obtener el plan requerido para una ruta
  const getRequiredPlanForRoute = (route: string): string => {
    const module = ROUTE_TO_MODULE[route];
    if (module) {
      return getMinimumPlanName(module);
    }
    // Buscar en rutas padre
    const routeParts = route.split('/').filter(Boolean);
    for (let i = routeParts.length - 1; i >= 0; i--) {
      const parentRoute = '/' + routeParts.slice(0, i + 1).join('/');
      const parentModule = ROUTE_TO_MODULE[parentRoute];
      if (parentModule) {
        return getMinimumPlanName(parentModule);
      }
    }
    return 'POS Premium';
  };

  // Obtener nombre amigable del módulo
  const getModuleName = (module: string): string => {
    return MODULE_NAMES[module] || module;
  };

  // Determinar si el plan requiere configuración contable completa
  // Los planes básicos de facturación no requieren períodos contables
  const basicPlans: PlanId[] = [
    'facturacion-prepago-75',
    'facturacion-prepago-150',
    'facturacion-prepago-350',
    'facturacion-prepago-750',
    'facturacion-150',
    'facturacion-200',
    'facturacion-300',
    'facturacion-500',
    'facturacion-1000',
    'facturacion-simple',
    'facturacion-premium',
  ];
  const requiresAccountingSetup = !basicPlans.includes(currentPlanId) && currentPlanId !== 'none';
  
  // skipPeriodValidation es true para planes básicos (no necesitan validar períodos)
  const skipPeriodValidation = basicPlans.includes(currentPlanId);

  return {
    currentPlanId,
    currentPlanName,
    isTrialActive,
    isTrialExpired,
    hasActivePlan,
    canAccessModule,
    canAccessRoute,
    getRequiredPlanForModule,
    getRequiredPlanForRoute,
    getModuleName,
    requiresAccountingSetup: adminPlanBypass ? true : requiresAccountingSetup,
    skipPeriodValidation: adminPlanBypass ? false : skipPeriodValidation,
    limits: planConfig?.limits || {
      users: 0,
      invoicesPerMonth: 0,
      warehouses: 0,
      products: 0,
    },
  };
}

export default usePlanPermissions;
